/**
 * Asking for a new UOM to be created — point 14's UOM half.
 *
 * `items.uom_creation_request` (live-probed 2026-09-03; the measurements and the duplicate-check
 * gap are recorded in this module's test). It files a `UOM Creation Request` document carrying the
 * unit and an optional conversion rate. Somebody then approves it.
 *
 * ---- It is a REQUEST, and nothing here may imply otherwise ----
 *
 * Filing one does not make the unit usable: the UOM master is unchanged until the request is
 * approved, so the line the purchaser was working on still cannot be set to it. Every string in
 * this flow says "requested", never "created" — a purchaser who believes the unit now exists will
 * go back to the row, fail to select it, and conclude the app is broken.
 *
 * ---- The item half is in `itemRequest.ts` ----
 *
 * This file used to say it did not exist, because the API collection's `I8 - Item Creation
 * Request` entry has no URL — a placeholder. That was true of the COLLECTION and false of the
 * server: `items.item_creation_request` is live, found by symmetry with this method on 2026-09-05.
 * See `itemRequest.ts` for the probe that turned it up.
 */

import { useMutation } from '@tanstack/react-query'
import { callGet } from './bosun'
import { withBosunError } from './useEnquiryMutations'
import type { BosunApiError } from './client'

export interface WireUomRequestResult {
  status?: string
  /** The new `UOM Creation Request` docname. */
  name?: string
}

/**
 * Why this request cannot be sent, or `null` if it can.
 *
 * The duplicate check is ours, not the server's: it accepted `uom=Nos` and filed a request for a
 * unit that plainly already exists. Unguarded, the approval queue fills with units the master
 * already has and whoever reviews it learns to ignore the whole queue.
 */
export function validateUomRequest(uom: string, existingUoms: readonly string[]): string | null {
  const trimmed = uom.trim()
  // The endpoint 500s on a missing `uom` — caught here so it never becomes a raw TypeError toast.
  if (trimmed === '') return 'Enter a unit to request.'

  const lower = trimmed.toLowerCase()
  const clash = existingUoms.find((u) => u.trim().toLowerCase() === lower)
  // Case-insensitive: a purchaser typing "kg" means the existing `KG`, and filing a request for it
  // is exactly the pointless duplicate this guard exists to prevent.
  if (clash) return `“${clash}” already exists — pick it on the line instead of requesting it.`

  return null
}

/**
 * Whether a unit can be requested FOR A LINE (2026-09-30). A request names its item, and the
 * approved unit is pulled onto that item (`uomSync.ts`), so with a matched item the question is
 * whether the ITEM sells in it — PCS exists in the master, yet a Nos-only item still needs it.
 * Without an item there is nothing to pull onto, and only a unit the master lacks is worth filing.
 */
export function uomRequestProblem(uom: string, existingUoms: readonly string[], itemUnits: readonly string[] | null): string | null {
  const trimmed = uom.trim()
  if (trimmed === '') return 'Enter a unit to request.'
  if (itemUnits === null) return validateUomRequest(uom, existingUoms)
  const lower = trimmed.toLowerCase()
  const clash = itemUnits.find((u) => u.trim().toLowerCase() === lower)
  return clash ? `“${clash}” is already on this item — pick it on the line.` : null
}

export interface UomRequestInput {
  uom: string
  conversionRate?: number
  /**
   * Where the unit is wanted (required by the server since 2026-09-19: `reference_name`, `row_id`,
   * `item`). `rowId` is the line's `enquiry_row_no`; `itemCode` the line's item, blank when it has
   * none — both accepted blank (measured).
   */
  enquiryId: string
  rowId?: string
  itemCode?: string | null
}

/** The exact query the method takes. */
export function uomRequestParams(input: UomRequestInput): Record<string, string | number> {
  return {
    uom: input.uom.trim(),
    // Optional on the wire (verified). Omitted rather than defaulted to 1: an unstated rate is
    // a question for whoever approves this, while `1` is a claim that the unit is the base.
    ...(input.conversionRate !== undefined && input.conversionRate > 0 ? { conversion_rate: input.conversionRate } : {}),
    reference_name: input.enquiryId,
    row_id: input.rowId ?? '',
    item: input.itemCode ?? '',
  }
}

export function useRequestUom(options?: { onSuccess?: (name: string, uom: string) => void }) {
  return useMutation<{ name: string; uom: string }, BosunApiError, UomRequestInput>({
    mutationFn: withBosunError(async (input) => {
      const { uom } = input
      const wire = await callGet<WireUomRequestResult>('items', 'uom_creation_request', uomRequestParams(input))
      if (wire?.status !== 'success' || !wire.name) {
        throw new Error('The UOM request was not filed — the server did not confirm it.')
      }
      return { name: wire.name, uom: uom.trim() }
    }),
    onSuccess: ({ name, uom }) => options?.onSuccess?.(name, uom),
  })
}
